import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Box,
  Alert,
  Button,
  Card,
  CardContent
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import DirectionsIcon from '@mui/icons-material/Directions';
import CampaignIcon from '@mui/icons-material/Campaign';
import DashboardOverview from "./DashboardOverview";
import CampaignService from "../../services/CampaignService";
import tenantService from "../../services/TenantService";
import { CampaignInfo } from "../../types/Campaign";
import { OrganizationDTO } from "../../types/Tenant";
import CreateFirstOrganization from './CreateFirstOrganization';
import CreateFirstCampaign from './CreateFirstCampaign';

const Dashboard: React.FC = () => {
  const [campaigns, setCampaigns] = useState<CampaignInfo[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationDTO[]>([]);
  const [selectedOrgId] = useState<string>('');
  const [isLoadingOrgs, setIsLoadingOrgs] = useState<boolean>(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isCreatingCampaign, setIsCreatingCampaign] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const campaignService = useMemo(() => new CampaignService(), []);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setIsLoadingOrgs(true);
        const fetchedOrganizations = await tenantService.getOrganizations();
        setOrganizations(fetchedOrganizations);

        setIsLoadingOrgs(false);
      } catch (error) {
        setIsLoadingOrgs(false);
      }
    };

    fetchOrganizations();
  }, []);

  useEffect(() => {
    // Only attempt to fetch campaigns after organizations are loaded
    if (organizations.length === 0 || isLoadingOrgs) {
      return;
    }

    const fetchCampaigns = async () => {
      try {
        setIsLoadingCampaigns(true);
        setError('');
        
        // Fetch campaigns for each organization and aggregate them
        const allCampaigns: CampaignInfo[] = [];
        
        for (const org of organizations) {
          if (org.id) {
            const orgCampaigns = await campaignService.getCampaigns(org.id);
            allCampaigns.push(...orgCampaigns);
          }
        }
        
        setCampaigns(allCampaigns);
      } catch (error) {
        setError('Unable to fetch campaigns');
      } finally {
        setIsLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
  }, [organizations, isLoadingOrgs, campaignService]);

  const handleCreateOrganization = () => {
    navigate('/organizations?create=true');
  };

  // Check subscription eligibility before creating a campaign
  const handleCreateCampaign = async (orgId: string) => {
    try {
      setIsCreatingCampaign(true);
      const eligibility = await campaignService.getSubscriptionEligibility(orgId);

      if (eligibility.can_create_campaign) {
        // User is eligible to create a campaign - create it directly
        const newCampaign = {
          organization_id: orgId,
          details: {
            name: 'New Mentorship Campaign'
          }
        };
        
        const campaign = await campaignService.createCampaign(orgId, newCampaign);
        
        if (!campaign) {
          throw new Error('Failed to create campaign');
        }
        
        // Navigate directly to the new campaign's overview page
        navigate(`/campaigns/${orgId}/${campaign.id}`);
      } else {
        // User needs to upgrade their subscription
        navigate(`/organizations/${orgId}`, {
          state: {
            expandSubscription: true,
            message: 'Your current subscription does not allow creating more campaigns. Please upgrade your plan.'
          }
        });
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      
      // Only display an error message for general errors
      // Don't redirect to subscription page for general errors
      setError('Failed to create campaign. Please try again later.');
      
      // We don't redirect to subscription page here as per requirement
      // Only subscription eligibility issues (handled above) should redirect to subscription page
    } finally {
      setIsCreatingCampaign(false);
    }
  };

  // Check if user has no organizations
  const hasNoOrganizations = !isLoadingOrgs && organizations.length === 0;

  // Check if user has organizations but no campaigns
  const hasOrganizationsButNoCampaigns =
    !isLoadingOrgs &&
    organizations.length > 0 &&
    campaigns.length === 0 &&
    selectedOrgId;

  // Find organization name by ID
  const getOrganizationName = (orgId: string): string => {
    const organization = organizations.find(org => org.id === orgId);
    return organization?.name || 'Unknown Organization';
  };

  return (
    <Container maxWidth="lg">
      {error && (
        <Box mb={2}>
          <Alert
            severity="error"
            sx={{
              backgroundColor: '#fdeded',
              color: '#5f2120',
              '& .MuiAlert-icon': {
                color: '#ef5350'
              }
            }}
          >
            {error}
          </Alert>
        </Box>
      )}

      {/* Show create first organization prompt if no organizations exist */}
      {hasNoOrganizations && (
        <CreateFirstOrganization />
      )}

      {/* Show organization tiles if organizations exist */}
      {!isLoadingOrgs && organizations.length > 0 && (
        <Box mb={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" component="h2">
              Your Organizations
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateOrganization}
            >
              New Organization
            </Button>
          </Box>

          <Grid container spacing={3}>
            {organizations.map((org) => (
              <Grid item xs={12} sm={6} md={4} key={org.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '80px',
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        borderRadius: 1,
                      }}
                    >
                      <Diversity3Icon sx={{ fontSize: 48 }} />
                    </Box>
                    <Typography gutterBottom variant="h6" component="div" noWrap>
                      {org.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {org.mission ? (
                        org.mission.length > 100 ? org.mission.substring(0, 97) + '...' : org.mission
                      ) : (
                        'No mission statement provided'
                      )}
                    </Typography>
                    <Box display="flex" justifyContent="space-between">
                      <Button
                        size="small"
                        onClick={() => navigate(`/organizations/${org.id}`, { state: { organization: org } })}
                        startIcon={<DirectionsIcon />}
                      >
                        Manage
                      </Button>
                      <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={() => org.id && handleCreateCampaign(org.id)}
                        startIcon={<CampaignIcon />}
                      >
                        New Campaign
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Show create first campaign prompt if organizations exist but no campaigns */}
      {hasOrganizationsButNoCampaigns && (
        <CreateFirstCampaign />
      )}

      {/* Show campaigns if they exist */}
      {campaigns.length > 0 && (
        <Grid container spacing={4}>
          {campaigns.map((campaign) => (
            <Grid item xs={12} md={6} key={campaign.id}>
              <DashboardOverview
                id={campaign.id}
                name={campaign.name}
                description={campaign.name || ''}
                organizationName={getOrganizationName(campaign.organizationId)}
                organizationId={campaign.organizationId}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default Dashboard;
